import { Col, Input, Radio, Row, Image, Button, Flex, Space } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";

import {
  EXAM_4PHOTOS_SLUGNAME,
  EXAM_AAII_SLUGNAME,
  EXAM_MAGNO_SLUGNAME,
  EXAM_VISION_SLUGNAME,
} from "./exams_definitions_utils";
import { TakenPhoto } from "./PosturalExamWrapper";

import frontGrid4FotoImg from "../images/grid_4photos_rot_270.png";
import frontGridAAIIImg from "../images/grid_aaii_rot_270.png";
import frontGridVisionmg from "../images/grid_vision_rot_270.png";
import { getPosturalExamPhoto, uploadPosturalExamPhoto } from "./utils";
import { useParams } from "react-router-dom";
import { SignedInDoctor } from "../auth/interfaces";
import { SignedInContext } from "../AppEnv";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BorderlessTableOutlined,
  BranchesOutlined,
  ClearOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";

import {
  PoseLandmarker,
  FilesetResolver,
  DrawingUtils,
  PoseLandmarkerOptions,
} from "@mediapipe/tasks-vision";
import { useCookies } from "react-cookie";
import { COOKIE_WC_HEIGHT } from "./exam_const";

const RIGHT_SHOULDER = 11;
const LEFT_SHOULDER = 12;

const getGridByExaminationType = (examType: string) => {
  switch (examType) {
    case EXAM_4PHOTOS_SLUGNAME:
      return frontGrid4FotoImg;
    case EXAM_AAII_SLUGNAME:
      return frontGridAAIIImg;
    case EXAM_VISION_SLUGNAME:
      return frontGridVisionmg;
    case EXAM_MAGNO_SLUGNAME:
      return frontGridVisionmg;
    default:
      return "";
  }
};

const showMediaPipeSkeleton = (examinationType: string) => {
  // console.log("showMediaPipeSkeleton " + examinationType);
  return (
    examinationType == EXAM_4PHOTOS_SLUGNAME ||
    examinationType == EXAM_AAII_SLUGNAME
  ); //TODO Leo aggiungi tu in or (||) gli esami per i quali vuoi lo skeletro generato con mediapipe
};

const generateGridOptions = () => [
  {
    label: (
      <>
        <SaveOutlined rev={undefined} />
        PDF
      </>
    ),
    value: "saved",
  },
  {
    label: <EditOutlined rev={undefined} />,
    value: "draw",
  },
  {
    label: (
      <>
        <EditOutlined rev={undefined} />
        <BorderlessTableOutlined rev={undefined} />
      </>
    ),
    value: "grid",
  },
];

interface CanvasPoint {
  x: number;
  y: number;
}

const computeDistance = (p1: CanvasPoint, p2: CanvasPoint) =>
  Math.sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y));

const POSE_CONNECTIONS: [number, number][] = [
  // Collo
  [11, 12],
  [12, 13],
  [13, 14],
  // Braccia
  [11, 15],
  [15, 17],
  [17, 19],
  [11, 16],
  [16, 18],
  [18, 20],
  // Torso
  [11, 23],
  [12, 24],
  [23, 24],
  // Gambe
  [23, 25],
  [25, 27],
  [27, 29],
  [29, 31],
  [24, 26],
  [26, 28],
  [28, 30],
  [30, 32],
];

// Before we can use PoseLandmarker class we must wait for it to finish
// loading. Machine Learning models can be large and take a moment to
// get everything needed to run.
const createPoseLandmarker = async (runningMode: any = "IMAGE") => {
  const vision = await FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
  );
  const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: `https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task`,
      delegate: "CPU", // TODO
    },
    runningMode: runningMode,
    numPoses: 1,
  });
  console.log("PoseLandmarker created");
  return poseLandmarker;
  //demosSection.classList.remove("invisible");
};

const ExamImage = ({
  index,
  image,
  examinationType,
  insertDrawnImage,
}: {
  index: number;
  image: TakenPhoto;
  examinationType: string;
  insertDrawnImage: Function;
}) => {
  let poseLandmarker: PoseLandmarker | undefined = undefined;
  const signedInDoctor: SignedInDoctor =
    useContext(SignedInContext).signedInDoctor;
  const { id } = useParams();
  const { examId } = useParams();

  const [selectedOption, setSelectedOption] = useState("saved");
  const [drawOption, setDrawOption] = useState("-");
  const [drawMode, setDrawMode] = useState(true);
  const [gridDrawn, setGridDrawn] = useState(false);
  const [cookies, setCookie] = useCookies([COOKIE_WC_HEIGHT]);
  const [webcamHeight, setWebcamHeight] = useState<number>(
    cookies[COOKIE_WC_HEIGHT] ? parseInt(cookies[COOKIE_WC_HEIGHT]) : 448
  );
  
  const [drawnImageUrl, setDrawnImageUrl] = useState<string | undefined>(
    undefined
  );
  const [lastCanvasPoint, setLastCanvasPoint] = useState<CanvasPoint>({
    x: 0,
    y: 0,
  });


  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);

  const [lineColor, setLineColor] = useState("#ff0000");
  const [circleColor, setCircleColor] = useState("#ffff00");

  const gridOptions = generateGridOptions()

  if (
    showMediaPipeSkeleton(examinationType) &&
    !gridOptions.find((elem) => elem.value == "skeleton")
  )
    gridOptions.push({
      label: (
        <>
          <BranchesOutlined rev={undefined} />
          Skeleton
        </>
      ),
      value: "skeleton",
    });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frontGridRef = useRef<HTMLImageElement>(null);
  const photoRef = useRef<HTMLImageElement>(null);

  const drawOptions = [
    {
      label: "I",
      value: "I",
      style: {
        backgroundColor:
          selectedOption == "saved"
            ? "gray"
            : drawOption == "I"
            ? "#409cd5"
            : "#1b366d",
        minWidth: "56px",
        color: "white",
      },
    },
    {
      label: "-",
      value: "-",
      style: {
        backgroundColor:
          selectedOption == "saved"
            ? "gray"
            : drawOption == "-"
            ? "#409cd5"
            : "#1b366d",
        minWidth: "56px",
        color: "white",
      },
    },
    {
      label: "/",
      value: "/",
      style: {
        backgroundColor:
          selectedOption == "saved"
            ? "gray"
            : drawOption == "/"
            ? "#409cd5"
            : "#1b366d",
        minWidth: "56px",
        color: "white",
      },
    },
    {
      label: "M",
      value: "M",
      style: {
        backgroundColor:
          selectedOption == "saved"
            ? "gray"
            : drawOption == "M"
            ? "#409cd5"
            : "#1b366d",
        minWidth: "56px",
        color: "white",
      },
    },
    {
      label: <BorderlessTableOutlined rev={undefined} />,
      value: "g",
      disabled: selectedOption != "draw" || gridDrawn,
      style: {
        backgroundColor:
          selectedOption != "draw"
            ? "gray"
            : drawOption == "g"
            ? "#409cd5"
            : "#1b366d",
        minWidth: "56px",
        color: "white",
      },
    },
  ];

  const updateDrawnImage = () => {
    if (id && examId)
      getPosturalExamPhoto(
        signedInDoctor,
        parseInt(id),
        parseInt(examId),
        index,
        true
      ).then((blobUrl) => {
        setDrawnImageUrl(blobUrl);
        if (blobUrl && blobUrl.length > 0) insertDrawnImage(blobUrl, index);
        else insertDrawnImage(image.url, index);
      });
  };

  const drawLine = (e: React.MouseEvent) => {
    if (drawOption == "g") return;
    if (canvasRef.current) {
      const ne: MouseEvent = e.nativeEvent;
      const canvas: HTMLCanvasElement = canvasRef.current;
      const ctx: CanvasRenderingContext2D | null = canvas.getContext("2d");

      if (ctx != null) {
        const curr_state = canvas.toDataURL();

        //LINE
        ctx.beginPath(); // Start a new path
        switch (drawOption) {
          case "-":
            ctx.moveTo(0, ne.offsetY); // Move the pen to (0, clicked y)
            ctx.lineTo(canvas.width, ne.offsetY); // Draw a line to (canvas.width, clicked y)
            break;
          case "I":
            ctx.moveTo(ne.offsetX, 0); // Move the pen to (0, clicked y)
            ctx.lineTo(ne.offsetX, canvas.height); // Draw a line to (canvas.width, clicked y)
            break;
          case "/":
            if (lastCanvasPoint.x > 0) 
            {
              ctx.moveTo(lastCanvasPoint.x, lastCanvasPoint.y);
              ctx.lineTo(ne.offsetX, ne.offsetY);
              ctx.font = "12px Verdana";
              ctx.fillStyle = circleColor;
              ctx.fillText(
                (
                  (100 *
                    computeDistance(lastCanvasPoint, {
                      x: ne.offsetX,
                      y: ne.offsetY,
                    })) /
                  canvas.width
                ).toFixed(2) + "%",
                (lastCanvasPoint.x + ne.offsetX) / 2 - 16,
                (lastCanvasPoint.y + ne.offsetY) / 2 + 16
              );
              //console.log(lastCanvasPoint, { x: ne.offsetX, y: ne.offsetY });
            }
            break;
          case "M":
            if (lastCanvasPoint.x > 0) 
            {
              ctx.moveTo(lastCanvasPoint.x, lastCanvasPoint.y);
              ctx.lineTo(ne.offsetX, ne.offsetY);
              ctx.fillStyle = circleColor;
              /*
              ctx.fillText(
                (
                  (100 *
                    computeDistance(lastCanvasPoint, {
                      x: ne.offsetX,
                      y: ne.offsetY,
                    })) /
                  canvas.width
                ).toFixed(2) + "%",
                (lastCanvasPoint.x + ne.offsetX) / 2 ,
                (lastCanvasPoint.y + ne.offsetY) / 2 
              );
              */
            }
            break;
        }
        ctx.lineWidth = 2;
        ctx.strokeStyle = lineColor;
        ctx.stroke();

        //CIRCLE POINT
        ctx.beginPath();
        ctx.arc(ne.offsetX, ne.offsetY, 3, 0, 2 * Math.PI, false);
        ctx.fillStyle = circleColor;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = circleColor;
        ctx.stroke();

        setLastCanvasPoint({ x: ne.offsetX, y: ne.offsetY });
        if ((drawOption == "/" || drawOption == "M") && lastCanvasPoint.x > 0) 
        {
          ctx.beginPath();
          ctx.arc(
            lastCanvasPoint.x,
            lastCanvasPoint.y,
            2,
            0,
            2 * Math.PI,
            false
          );
          ctx.fillStyle = circleColor;
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = circleColor;
          ctx.stroke();
          if (drawOption == "M") {
            //CIRCLE POINT
            ctx.beginPath();
            ctx.arc(
              (lastCanvasPoint.x + ne.offsetX) / 2,
              (lastCanvasPoint.y + ne.offsetY) / 2,
              2,
              0,
              2 * Math.PI,
              false
            );
            ctx.fillStyle = circleColor;
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = circleColor;
            ctx.stroke();
          }
          //forget last clicked canvas point to draw a new line
          setLastCanvasPoint({ x: -1, y: -1 });
        }

        setUndoStack([...undoStack, curr_state])
      }

      if (id && examId)
        canvas.toBlob((blob: Blob | null) => {
          if (blob != null)
            uploadPosturalExamPhoto(
              blob,
              signedInDoctor,
              parseInt(id),
              parseInt(examId),
              index,
              true
            ).then((uploadedFilename) => {
              if (uploadedFilename.indexOf("d.png") > 0) {
                updateDrawnImage();
              }
            });
        });
    }
  };

  const cleanDrawImage = async () => {
    setUndoStack([]);
    setRedoStack([]);
    drawPhoto();
    const blob = await (await fetch(image.url)).blob();
    if (id && examId)
      uploadPosturalExamPhoto(
        blob,
        signedInDoctor,
        parseInt(id),
        parseInt(examId),
        index,
        true
      ).then((uploadedFilename) => {
        if (uploadedFilename.indexOf("d.png") > 0) {
          updateDrawnImage();
        }
      });
  };

  const restoreImageCanvasFromDataUrl = (dataUrl : string) => 
  {
    if (canvasRef.current)
    {
      const canvas: HTMLCanvasElement = canvasRef.current;
      const ctx: CanvasRenderingContext2D | null = canvas.getContext("2d");
      if (!ctx) return;
      const img = new window.Image();
      img.src = dataUrl;
      console.log("ABOUT TO LOAD OLD IMAGE")
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        console.log("OLD IMAGE LOADED")
      };
    }
  };

  const editBackward = 
  () => 
  {
    if (undoStack.length === 0) return;
    const lastState:string = undoStack.pop()!;
    setUndoStack([...undoStack]);
    if(canvasRef.current) setRedoStack((prev) => [canvasRef.current!.toDataURL(), ...prev]);
    restoreImageCanvasFromDataUrl(lastState);
  };

  const editForward = 
  () => {
    if (redoStack.length === 0) return;
    const nextState = redoStack.shift();
    if (nextState) {
      setRedoStack([...redoStack]);
      const currentCanvasState = canvasRef.current?.toDataURL();
      if (currentCanvasState) {
        setUndoStack((prev) => [...prev, currentCanvasState]);
      }
      restoreImageCanvasFromDataUrl(nextState);
    }
  };


  const drawGrid = () => {
    if (canvasRef.current && frontGridRef.current && photoRef.current) {
      const canvas: HTMLCanvasElement = canvasRef.current;
      const photo: HTMLImageElement = photoRef.current;
      const frontGrid: HTMLImageElement = frontGridRef.current;
      const ctx: CanvasRenderingContext2D | null = canvas.getContext("2d");
      if (ctx != null) {
        ctx.drawImage(frontGrid, 0, 0, photo.width, photo.height);
      }
    }
  };

  let i = 0;
  const drawPhoto = async () => {
    if (canvasRef.current && frontGridRef.current && photoRef.current) {
      const canvas: HTMLCanvasElement = canvasRef.current;
      const photo: HTMLImageElement = photoRef.current;
      const frontGrid: HTMLImageElement = frontGridRef.current;

      const ctx: CanvasRenderingContext2D | null = canvas.getContext("2d");
      if (ctx != null) {
        // Definisci la dimensione massima desiderata per il canvas
        const maxWidth = webcamHeight * 1.53;//650; // Cambia il valore di maxWidth secondo le tue esigenze
        const maxHeight = webcamHeight;//425; // Cambia il valore di maxHeight secondo le tue esigenze

        // Calcola il fattore di ridimensionamento per adattare la foto alla dimensione massima desiderata
        const scaleFactor = Math.min(
          maxWidth / photo.width,
          maxHeight / photo.height
        );

        // Calcola le nuove dimensioni della foto
        const newWidth = photo.width * scaleFactor;
        const newHeight = photo.height * scaleFactor;

        // Imposta le nuove dimensioni del canvas
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Disegna la foto ridimensionata sul canvas
        ctx.drawImage(photo, 0, 0, newWidth, newHeight);

        // Disegna la griglia se l'opzione è selezionata
        if (selectedOption === "grid") {
          ctx.drawImage(frontGrid, 0, 0, newWidth, newHeight);
        }

        if (selectedOption === "skeleton") {
          //mediapipe google integration
          if (!poseLandmarker) {
            console.log("Wait for poseLandmarker to load before clicking!");
            poseLandmarker = await createPoseLandmarker("IMAGE");
          }

          if (poseLandmarker) {
            poseLandmarker.detect(photo, (result) => {
              if (photo.parentNode) {
                //photo.parentNode.appendChild(canvas);
                //const canvasCtx = canvas.getContext("2d");
                if (ctx) {
                  const drawingUtils = new DrawingUtils(ctx);
                  console.log(result.landmarks);
                  for (const landmarksPerson of result.landmarks) {
                    for (let i = 0; i <= landmarksPerson.length; i++) {
                      if (i < 11) {
                        landmarksPerson[i].x = -0.01;
                        landmarksPerson[i].y = -0.01;
                      }
                      /*
if (i < 11 || (i > 16 && i < 23) || (i > 27 && i < 32)) {
                        landmarksPerson[i].x = -0.01;
                        landmarksPerson[i].y = -0.01;
                      }
                      */

                      const filteredConnections = POSE_CONNECTIONS.filter(
                        (connection) =>
                          landmarksPerson[connection[0]].visibility === 0 ||
                          landmarksPerson[connection[1]].visibility === 0
                      );
                    }

                    // CHECKS
                    if (
                      Math.abs(
                        landmarksPerson[RIGHT_SHOULDER].y -
                          landmarksPerson[LEFT_SHOULDER].y
                      ) > 0.01
                    )
                      console.log("Spalle disallineate");

                    drawingUtils.drawLandmarks(landmarksPerson, {
                      radius: (data) =>
                        DrawingUtils.lerp(data.from!.z, -0.15, 0.1, 3, 1),
                      color: "yellow",
                      fillColor: "yellow",
                      lineWidth: 1.0,
                    });
                    drawingUtils.drawConnectors(
                      landmarksPerson,
                      PoseLandmarker.POSE_CONNECTIONS,
                      {
                        color: "white",
                        fillColor: "blue",
                        lineWidth: 0.5,
                      }
                    );
                  }
                }
              }
            });
          }
        }
      }
    }
  };

  const heightP = photoRef.current?.height ?? 0;
  const heightP2 = () => heightP * 0.55;

  const widthP = photoRef.current?.height ?? 0;
  const widthP2 = () => widthP * 0.55;

  useEffect(() => {
    setSelectedOption("saved");
    setDrawOption("-");
    if (canvasRef.current && photoRef.current) {
      const canvas: HTMLCanvasElement = canvasRef.current;
      const photo: HTMLImageElement = photoRef.current;
      const ctx: CanvasRenderingContext2D | null = canvas.getContext("2d");
      if (ctx != null) {
        // Definisci la dimensione massima desiderata per il canvas
        const maxWidth = 650; // Cambia il valore di maxWidth secondo le tue esigenze
        const maxHeight = 425; // Cambia il valore di maxHeight secondo le tue esigenze

        // Calcola il fattore di ridimensionamento per adattare la foto alla dimensione massima desiderata
        const scaleFactor = Math.min(
          maxWidth / photo.width,
          maxHeight / photo.height
        );

        // Calcola le nuove dimensioni della foto
        const newWidth = photo.width * scaleFactor;
        const newHeight = photo.height * scaleFactor;

        // Imposta le nuove dimensioni del canvas
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Disegna la foto ridimensionata sul canvas
        ctx.drawImage(photo, 0, 0, newWidth, newHeight);

        // Disegna la griglia se l'opzione è selezionata
        if (selectedOption === "grid") {
          ctx.drawImage(photo, 0, 0, newWidth, newHeight);
        }
      }
    }
    updateDrawnImage();
  }, [image, index]);

  useEffect(() => {
    setUndoStack([]);
    setRedoStack([]);
    setGridDrawn(false);
    drawPhoto();
    setDrawMode(selectedOption != "saved");
  }, [selectedOption, webcamHeight]);

  useEffect(() => {
    if (drawOption == "/") setLastCanvasPoint({ x: -1, y: -1 });

    switch (drawOption) {
      case "I":
        setLineColor("#ffffff");
        setCircleColor("#ff0000");
        break;
      case "-":
        setLineColor("#ff0000");
        setCircleColor("#ffff00");
        break;
      case "/":
        setLineColor("#00ff00");
        setCircleColor("#ffffff");
        break;
      case "M":
        setLineColor("#0000ff");
        setCircleColor("#00ff00");
        break;
      case "g":
        if (!gridDrawn) drawGrid();
        setGridDrawn(true);
        break;
    }
  }, [drawOption]);

  const [width, setWidth] = useState(0);
  const handleResize = () => setWidth(window.innerWidth);

  useEffect(() => {
    //mount
    window.addEventListener("resize", handleResize);
    setWidth(window.innerWidth);
    //unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {width > 640 && (
        <div className="centered">
          <div>
            <Space size="small">
              <label>Dimensione immagine:  </label>
              <Input
                  className="webbo"
                  defaultValue={webcamHeight}
                  aria-label="Dimensione immagine"
                  type="range"
                  min={400}
                  step={50}
                  max={1200}
                  onChange={(e) => {
                    setWebcamHeight(parseInt(e.target.value));
                    setCookie(COOKIE_WC_HEIGHT, e.target.value, {
                      path: "/",
                      expires: new Date(
                        new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 5
                      ), //expires in 5 years
                    });
                  }}
              />
            </Space>
          </div>
          <img
            ref={frontGridRef}
            style={{
              borderRadius: "25px",
              display: "none",
            }}
            src={getGridByExaminationType(examinationType)}
          />
          <img ref={photoRef} style={{ display: "none" }} src={image.url} />

          <canvas
            style={{
              borderRadius: "25px",
              display: drawMode ? "initial" : "none",
            }}
            ref={canvasRef}
            onMouseUp={(e) => drawLine(e)}
          />
          <Image
            style={{
              maxHeight: webcamHeight,
              borderRadius: "25px",
              display: drawMode ? "none" : "initial",
            }}
            src={drawnImageUrl || image.url}
          />

          <div
            className="centered"
            style={{
              maxHeight: "30rem",
              borderRadius: "25px",
              maxWidth: canvasRef.current?.width,
            }}
          >
            <Radio.Group
              className="exam-image-btn"
              style={{
                marginTop: drawMode ? "0px" : "8px",
              }}
              options={gridOptions}
              onChange={(e) => setSelectedOption(e.target.value)}
              value={selectedOption}
              optionType="button"
              buttonStyle="solid"
            />
          </div>
          <div
            className="centered"
            style={{
              maxWidth: canvasRef.current?.width,
              marginTop: "4px",
            }}
          >
            <Row>
              <Col span={24} style={{ padding: "4px" }}>
                <Row>
                  <Col span={4}>
                    <label style={{ color: drawMode ? "black" : "gray" }}>
                      Linea:{" "}
                    </label>
                    <Input
                      disabled={!drawMode}
                      value={lineColor}
                      type="color"
                      onChange={(e) => setLineColor(e.target.value)}
                    />
                  </Col>
                  <Col span={16}>
                    <Radio.Group
                      disabled={!drawMode}
                      className="ant-radio-black"
                      options={drawOptions}
                      onChange={(e) => setDrawOption(e.target.value)}
                      value={drawOption}
                      optionType="button"
                      buttonStyle="outline"
                    />
                  </Col>
                  <Col span={4} style={{ padding: "4px" }}>
                    <label style={{ color: drawMode ? "black" : "gray" }}>
                      Punto:{" "}
                    </label>
                    <Input
                      value={circleColor}
                      type="color"
                      disabled={!drawMode}
                      onChange={(e) => setCircleColor(e.target.value)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          <div className="centered">
            <Space size={"middle"}>
              <div>
                <Button className="custom-btn-edit" disabled={!drawMode || undoStack.length == 0} onClick={() => editBackward()}><ArrowLeftOutlined rev={undefined}/></Button>
                <Button className="custom-btn-edit" disabled={!drawMode || redoStack.length == 0}  onClick={() => editForward()}><ArrowRightOutlined rev={undefined}/></Button>
              </div>
              <Button className="custom-btn-edit" onClick={() => cleanDrawImage()}><ClearOutlined rev={undefined} /></Button>
            </Space>
          </div>
        </div>
      )}
      {width < 640 && (
        <Image
          style={{
            maxHeight: "600px !important",
            borderRadius: "25px",
            display: drawMode ? "none" : "initial",
          }}
          src={drawnImageUrl || image.url}
        />
      )}
    </>
  );
};

export default ExamImage;
